import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { dashPath } from "../lib/utils-trel";
import Logo from "../components/Logo";
import PreviewModeEntry from "../components/PreviewModeEntry";
import { Check, User as UserIcon, Building2 } from "lucide-react";

export default function Signup(){
  const { signup } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Honor ?role=... deep links from /become-provider / /become-host.
  const initialRole = useMemo(() => {
    const r = searchParams.get("role");
    return ["customer", "provider", "host"].includes(r) ? r : "customer";
  }, [searchParams]);
  const [role, setRole] = useState(initialRole);
  // Concierge sub-type — only relevant when role === "provider".
  const [accountType, setAccountType] = useState("individual");
  const [form, setForm] = useState({ name:"", email:"", password:"", phone:"", businessName:"" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form, role };
      if (role === "provider") {
        payload.account_type = accountType;
        if (accountType === "business") {
          payload.business_name = form.businessName || form.name;
        }
      }
      const u = await signup(payload);
      nav(dashPath(u.role));
    } catch(e) { setErr(e.response?.data?.detail || "Signup failed"); }
    setBusy(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/customer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1400" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[#064E3B]/70 to-[#071A2D]/70"/>
        <div className="absolute bottom-12 left-12 right-12 text-[#FFF8EE]">
          <img src="/trel-logo.png" alt="" className="h-12 w-auto opacity-95" draggable={false}/>
          <div className="font-serif text-4xl mt-5 leading-tight" style={{color:"#FFF8EE"}}>Your AI concierge<br/>for unforgettable moments.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-10 bg-[#FFF8EE]">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block"><Logo size="lg"/></Link>
          <h1 className="font-serif text-3xl mt-8">Create your account</h1>
          <p className="text-sm text-[#071A2D]/60 mt-1">How would you like to use TREL?</p>

          <div className="grid grid-cols-3 gap-2 mt-5">
            {[["customer","Find experiences"],["provider","Provide"],["host","Add to property"]].map(([k,l])=>(
              <button key={k} data-testid={`role-${k}`} onClick={()=>setRole(k)}
                className={`p-3 rounded-2xl text-xs border ${role===k?"bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]":"border-[#071A2D]/15"}`}>
                {l}
              </button>
            ))}
          </div>

          {/* Concierge sub-type picker — only when role === "provider". Free to join either way. */}
          {role === "provider" && (
            <div className="mt-6" data-testid="provider-account-type">
              <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">I am joining as</label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="acct-type-individual"
                  onClick={() => setAccountType("individual")}
                  className={`text-left p-4 rounded-2xl border transition ${accountType==="individual" ? "border-[#064E3B] bg-[#064E3B]/8" : "border-[#071A2D]/15 hover:border-[#071A2D]/30"}`}
                >
                  <div className="flex items-center gap-2">
                    <UserIcon size={16} className="text-[#064E3B]"/>
                    <div className="font-serif text-sm text-[#071A2D]">Individual Concierge</div>
                  </div>
                  <div className="text-[11px] text-[#071A2D]/65 mt-1.5 leading-relaxed">
                    For independent experts who create unforgettable experiences.
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="acct-type-business"
                  onClick={() => setAccountType("business")}
                  className={`text-left p-4 rounded-2xl border transition ${accountType==="business" ? "border-[#064E3B] bg-[#064E3B]/8" : "border-[#071A2D]/15 hover:border-[#071A2D]/30"}`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-[#064E3B]"/>
                    <div className="font-serif text-sm text-[#071A2D]">Business / Experience Company</div>
                  </div>
                  <div className="text-[11px] text-[#071A2D]/65 mt-1.5 leading-relaxed">
                    For businesses that create, manage, or deliver premium experiences.
                  </div>
                </button>
              </div>
              <div
                data-testid="free-to-join-checklist"
                className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#064E3B] font-medium"
              >
                <span className="inline-flex items-center gap-1"><Check size={11} strokeWidth={3}/> Free to join</span>
                <span className="inline-flex items-center gap-1"><Check size={11} strokeWidth={3}/> Build your profile</span>
                <span className="inline-flex items-center gap-1"><Check size={11} strokeWidth={3}/> Receive qualified customer requests</span>
                <span className="inline-flex items-center gap-1"><Check size={11} strokeWidth={3}/> Earn from completed experiences</span>
              </div>
            </div>
          )}

          <div className="mt-6 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">
                {role === "provider" && accountType === "business" ? "Your name (account owner)" : "Name"}
              </label>
              <input data-testid="signup-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="kbd-input"/>
            </div>
            {role === "provider" && accountType === "business" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">Business name</label>
                <input
                  data-testid="signup-business-name"
                  value={form.businessName}
                  onChange={e=>setForm({...form, businessName: e.target.value})}
                  placeholder="e.g. DC Elite Experiences"
                  className="kbd-input"
                />
              </div>
            )}
            <div><label className="text-xs uppercase tracking-widest text-[#064E3B]/70">Email</label>
              <input data-testid="signup-email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="kbd-input"/></div>
            <div><label className="text-xs uppercase tracking-widest text-[#064E3B]/70">Password</label>
              <input data-testid="signup-password" type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="kbd-input"/></div>
          </div>

          {err && <div className="mt-4 text-sm text-[#FF7A45]">{err}</div>}
          <button data-testid="signup-submit" onClick={submit} disabled={busy} className="btn-coral w-full mt-7">{busy?"Creating…":"Create account"}</button>

          <div className="flex items-center gap-3 my-5 text-xs text-[#071A2D]/40"><div className="flex-1 h-px bg-[#071A2D]/10"/>OR<div className="flex-1 h-px bg-[#071A2D]/10"/></div>
          <button data-testid="signup-google" onClick={googleLogin} className="w-full border border-[#071A2D]/15 rounded-full py-3 font-medium hover:bg-[#FFF8EE]">Continue with Google</button>

          <div className="text-sm text-center mt-6">Already have an account? <Link to="/login" className="underline">Log in</Link></div>

          {/* Preview Mode — primary entry point. New visitors can explore
              TREL with sample data before committing to creating an account
              (Phase 33). Reduces signup hesitation. */}
          <PreviewModeEntry variant="signup" testidPrefix="signup-preview" />
        </div>
      </div>
    </div>
  );
}
